<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <h1 class="title">Page Permissions</h1>
      </v-col>
    </v-row>
    <v-alert
      type="warning"
      class="mt-6"
      v-if="!isLoading && (!permissionGroups || permissionGroups.length === 0)"
      >No permission groups for client - please set up in
      <router-link to="workflow">Workflow Admin</router-link></v-alert
    >
    <v-row v-else-if="!isLoading">
      <v-col>
        <v-tabs v-model="tab">
          <v-tab>Permissions</v-tab>
          <v-tab>Meta Admin</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
        <v-card width="100%" class="pa-4">
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="py-2"><h2>Section</h2></th>
                  <th class="py-2">
                    <h2>
                      Page
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on">
                            mdi-filter-menu
                          </v-icon>
                        </template>
                        <v-list>
                          <v-list-item
                            v-for="(f, fi) in routeFilter.list"
                            :key="'f' + fi"
                            @click="filterRoutes(f)"
                          >
                            <v-list-item-title
                              ><b v-if="routeFilter.value === f">{{ f }}</b
                              ><span v-else>{{ f }}</span></v-list-item-title
                            >
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </h2>
                  </th>
                  <th class="py-2" colspan="8">
                    <h2>Permission Groups</h2>
                  </th>
                </tr>
                <tr>
                  <th colspan="2"></th>
                  <th v-for="pg in permissionGroups" :key="pg.pg_id">
                    <v-chip small class="mb-2">{{
                      pg.permission_group_name
                    }}</v-chip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, ri) in routeFilter.routes" :key="'r' + ri">
                  <td>
                    {{ r.section }}
                  </td>
                  <td>
                    <v-icon v-if="r.icon" :class="r.icon">{{
                      r.icon.split(" ")[0]
                    }}</v-icon>
                    {{ r.label }}
                    <v-btn
                      v-if="r.functions.length"
                      icon
                      small
                      title="Edit page functions"
                      @click="openFunctions(r)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                  <td v-for="pg in permissionGroups" :key="pg.pg_id">
                    <v-btn
                      icon
                      title="Disable"
                      v-if="r[`pg${pg.permission_group_id}_enabled`]"
                      @click="toggleRoute(r, pg.permission_group_id)"
                    >
                      <v-icon>mdi-check-circle-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      v-else
                      title="Enable"
                      style="opacity: 0.2"
                      @click="toggleRoute(r, pg.permission_group_id)"
                    >
                      <v-icon>mdi-plus-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>
          </v-tab-item>
          <v-tab-item>
            <RouteMetaAdmin/>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
    <v-dialog v-model="functionsDialog.show" max-width="1000" scrollable>
      <v-card v-if="functionsDialog.route">
        <v-card-title>
          {{ functionsDialog.route.label }} - page functions
          <v-spacer></v-spacer>
          <v-btn icon title="Close" @click="functionsDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle class="pt-2">
          Only permission groups with access to {{ functionsDialog.route.label }} are shown.
          A function is available to a user who can access the page and holds the function through any of their groups.
        </v-card-subtitle>
        <v-card-text>
          <v-alert v-if="!functionsDialogGroups.length" type="info" text dense>
            No permission groups can access {{ functionsDialog.route.label }} yet.
          </v-alert>
          <v-simple-table v-else dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Function</th>
                  <th v-for="pg in functionsDialogGroups" :key="pg.permission_group_id" class="text-center">
                    <v-chip small>{{ pg.permission_group_name }}</v-chip>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in functionsDialog.route.functions" :key="f.name">
                  <td>{{ f.text }}</td>
                  <td v-for="pg in functionsDialogGroups" :key="pg.permission_group_id" class="text-center">
                    <v-btn
                      icon
                      :loading="isFunctionSaving(functionsDialog.route.name, f.name, pg.permission_group_id)"
                      :title="hasFunction(functionsDialog.route.name, f.name, pg.permission_group_id) ? 'Disable' : 'Enable'"
                      :style="hasFunction(functionsDialog.route.name, f.name, pg.permission_group_id) ? '' : 'opacity: 0.2'"
                      @click="toggleFunction(functionsDialog.route, f, pg.permission_group_id)"
                    >
                      <v-icon>{{ hasFunction(functionsDialog.route.name, f.name, pg.permission_group_id) ? 'mdi-check-circle-outline' : 'mdi-plus-outline' }}</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="functionsDialog.show = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Loading :isVisible="isLoading" />
    <ResponseHandler :serviceResponse="response"></ResponseHandler>
  </v-container>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import ResponseHandler from "@/components/ResponseHandler";
import RouteMetaAdmin from '../components/admin/RouteMetaAdmin.vue'

export default {
  name: "vRouteAdmin",
  components: {
    ResponseHandler,
    RouteMetaAdmin,
  },
  props: {},
  data: function () {
    return {
      isLoading: true,
      permissionGroups: [],
      clientRoutes: [],
      response: null,
      tab: null,
      permittedFunctions: [],
      savingFunctions: [],
      functionsDialog: {
        show: false,
        route: null,
      },
      routeFilter: {
        list: ["All", "Client Visible", "RoleMapper Admin", "Module Pages"],
        value: "Client Visible",
        routes: [],
      },
    };
  },
  mounted() {},
  created() {
    this.init();
  },
  watch: {},
  computed: {
    ...mapState({
      newUi: (state) => state.theme.newUi,
      docsLoading: (state) => state.docs.loading,
      docStatusSummary: (state) => state.docs.docStatusSummary,
      docTypeSummary: (state) => state.docs.docTypeSummary,
      hierarchiesLoading: (state) => state.hierarchies.loading,
      docTypes: (state) => state.hierarchies.docTypes,
    }),
    functionsDialogGroups() {
      const route = this.functionsDialog.route;
      if (!route) return [];
      return this.permissionGroups.filter((pg) => route[`pg${pg.permission_group_id}_enabled`]);
    },
  },
  methods: {
    sessionExpired(err) {
      this.$emit("sessionExpired", err);
    },
    init() {
      let possibleError = false;
      this.isLoading = true;
      axios
        .get("admin/routeAdminLoadData")
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            const data = resp.data.Data;
            const permittedRoutes = data.permittedRoutes;
            const routes = data.routes;
            const clientSettings = this.$loginState.user.settings;
            const permissionGroups = data.permissionGroups;
            this.clientRoutes = this.$router
              .getRoutes()
              .filter(
                (r) =>
                  r.meta?.newUi && !r.meta?.disabled && !r.meta.landingPageId && !r.meta.internal
                  && (r.meta?.module_setting ? clientSettings.find(x => x.setting === r.meta.module_setting && x.value === 'true') : true)
              )
              .map((r, ri) => {
                const route = {
                  label: r.meta?.text || r.name,
                  icon: r.meta?.icon,
                  section: r.meta?.section || "",
                  name: r.name,
                  path: r.path,
                  module: r.meta?.module || null,
                  functions: r.meta?.functions || [],
                  sectionOrder: this.$router.menuSections.findIndex(
                    (s) => s.name === r.meta?.section
                  ),
                  order: ri,
                  pr_id: permittedRoutes.find((pr) => pr.route_name === r.name)
                    ?.pr_id,
                  reserved:
                    r.meta?.reserved ||
                    routes.find((pr) => pr.route_name === r.name)?.reserved,
                };
                permissionGroups.forEach((pg) => {
                  const pr = permittedRoutes.find(
                    (pr) =>
                      pr.route_name === r.name &&
                      pg.permission_group_id === pr.permission_group_id
                  );
                  route[`pg${pg.permission_group_id}_enabled`] = !!pr;
                });
                return route;
              })
              .sort((a, b) =>
                a.sectionOrder > b.sectionOrder
                  ? 1
                  : a.sectionOrder === b.sectionOrder
                  ? a.order > b.order
                    ? 1
                    : -1
                  : -1
              );
            this.permissionGroups = permissionGroups;
            this.permittedFunctions = (data.permittedFunctions || []).map((f) => this.functionKey(f.route_name, f.function_name, f.permission_group_id));
            this.filterRoutes("Client Visible");
          }
          this.response = resp.data;
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        });
    },
    toggleRoute(route, pgId) {
      let pgprId = route[`pg${pgId}_enabled`];

      if (pgprId) {
        let possibleError = false;
        axios
          .post("admin/routeAdminRemoveRoute", {
            routeName: route.name,
            permissionGroupId: pgId,
          })
          .then((resp) => {
            possibleError = true;
            if (resp.data.Status === "OK") {
              route[`pg${pgId}_enabled`] = false;
              // the server revokes the group's functions on this page too
              const revoked = route.functions.map((f) => this.functionKey(route.name, f.name, pgId));
              this.permittedFunctions = this.permittedFunctions.filter((k) => !revoked.includes(k));
            }
            this.response = resp.data;
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      } else {
        let possibleError = false;
        const defaultFunctions = route.functions.filter((f) => f.default).map((f) => f.name);
        axios
          .post("admin/routeAdminAddRoute", {
            routeName: route.name,
            permissionGroupId: pgId,
            defaultFunctions,
          })
          .then((resp) => {
            possibleError = true;
            if (resp.data.Status === "OK") {
              route[`pg${pgId}_enabled`] = true;
              // the server grants the page's default functions with it
              defaultFunctions
                .map((f) => this.functionKey(route.name, f, pgId))
                .filter((k) => !this.permittedFunctions.includes(k))
                .forEach((k) => this.permittedFunctions.push(k));
            }
            this.response = resp.data;
          })
          .catch((err) => {
            if (possibleError) {
              alert("Code Error");
            } else if (err.response && err.response.status === 401) {
              this.$emit("sessionExpired", err);
            } else {
              alert(err.response ? err.response.data.message : err);
            }
            console.log(err);
          });
      }
    },
    functionKey(routeName, functionName, pgId) {
      return `${routeName}|${functionName}|${pgId}`;
    },
    hasFunction(routeName, functionName, pgId) {
      return this.permittedFunctions.includes(this.functionKey(routeName, functionName, pgId));
    },
    isFunctionSaving(routeName, functionName, pgId) {
      return this.savingFunctions.includes(this.functionKey(routeName, functionName, pgId));
    },
    openFunctions(route) {
      this.functionsDialog.route = route;
      this.functionsDialog.show = true;
    },
    toggleFunction(route, func, pgId) {
      const key = this.functionKey(route.name, func.name, pgId);
      if (this.savingFunctions.includes(key)) return;
      const enabled = this.permittedFunctions.includes(key);
      let possibleError = false;
      this.savingFunctions.push(key);
      axios
        .post(enabled ? "admin/routeAdminRemoveFunction" : "admin/routeAdminAddFunction", {
          routeName: route.name,
          functionName: func.name,
          permissionGroupId: pgId,
        })
        .then((resp) => {
          possibleError = true;
          if (resp.data.Status === "OK") {
            if (enabled) this.permittedFunctions = this.permittedFunctions.filter((k) => k !== key);
            else this.permittedFunctions.push(key);
          }
          this.response = resp.data;
        })
        .catch((err) => {
          if (possibleError) {
            alert("Code Error");
          } else if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            alert(err.response ? err.response.data.message : err);
          }
          console.log(err);
        })
        .finally(() => {
          this.savingFunctions = this.savingFunctions.filter((k) => k !== key);
        });
    },
    filterRoutes(value) {
      this.routeFilter.value = value;
      switch (this.routeFilter.value) {
        case "All":
          this.routeFilter.routes.splice(0);
          this.routeFilter.routes = [...this.clientRoutes];
          break;
        case "Client Visible":
          this.routeFilter.routes.splice(0);
          this.routeFilter.routes = [
            ...this.clientRoutes.filter((x) => !x.reserved),
          ];
          break;
        case "RoleMapper Admin":
          this.routeFilter.routes.splice(0);
          this.routeFilter.routes = [
            ...this.clientRoutes.filter((x) => x.reserved),
          ];
          break;
        case "Module Pages":
          this.routeFilter.routes.splice(0);
          this.routeFilter.routes = [
            ...this.clientRoutes.filter((x) => x.reserved && x.module),
          ];
          break;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/vars";
</style>